'use client';

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { track } from '@upms/analytics';
import { reporter } from '@upms/observability';
import { useAuthContext } from '@/components/AuthContext';
import { useFeature } from '@/components/FeatureContext';
import { useIdentityContext } from '@/components/IdentityContext';
import { CONSENT_EVENTS } from '@/lib/analytics/events';
import { useMyConsents } from '@/lib/api/consents';
import { markDismissedThisSignIn, wasDismissedThisSignIn } from './consentPromptSession';
import { deriveConsentPrompt, type ConsentPrompt } from './deriveConsentPrompt';

export type UseConsentPromptResult =
  | { open: false }
  | {
      open: true;
      prompt: Extract<ConsentPrompt, { kind: 'prompt' }>;
      onOpenChange: (open: boolean) => void;
    };

const useAuthTime = () => {
  const { getSession } = useAuthContext();

  return useQuery({
    queryKey: ['auth', 'authTime'],
    queryFn: async () => (await getSession()).authTime ?? null,
    staleTime: Infinity,
    retry: false,
  });
};

const useReportConsentFetchError = (
  status: ReturnType<typeof useMyConsents>['status'],
  error: ReturnType<typeof useMyConsents>['error']
) => {
  const hasReported = useRef(false);

  useEffect(() => {
    if (status !== 'error' || hasReported.current) return;
    hasReported.current = true;
    reporter.error(
      'Consent state fetch failed',
      { feature: 'consent', operation: 'getMyConsents' },
      error
    );
  }, [status, error]);
};

const useSignInDismissal = (userId: string) => {
  const authTime = useAuthTime();
  const resolved = authTime.status === 'success';

  return {
    dismissed: resolved && wasDismissedThisSignIn(userId, authTime.data),
    markDismissed: () => {
      if (resolved) markDismissedThisSignIn(userId, authTime.data);
    },
  };
};

const reportPromptDisplayed = (pendingTypesKey: string) => {
  track(CONSENT_EVENTS.consentPromptDisplayed, { pending_types: pendingTypesKey });
  reporter.action('consent_prompt_displayed', {
    feature: 'consent',
    pending_type_count: pendingTypesKey ? pendingTypesKey.split(',').length : 0,
  });
};

export const useConsentPrompt = (): UseConsentPromptResult => {
  const { user } = useIdentityContext();
  const flagEnabled = useFeature('feature-upms-consent-prompt', false);
  const { status, error, data } = useMyConsents({ enabled: flagEnabled });
  const dismissal = useSignInDismissal(user.userId);

  const [phase, setPhase] = useState<'armed' | 'open' | 'settled'>('armed');

  useReportConsentFetchError(status, error);

  const prompt = status === 'success' ? deriveConsentPrompt(data.data.state) : null;
  const pendingTypesKey = prompt?.kind === 'prompt' ? prompt.missing.join(',') : '';
  const shouldOpen =
    phase === 'armed' && flagEnabled && prompt?.kind === 'prompt' && !dismissal.dismissed;

  useEffect(() => {
    if (!shouldOpen) return;
    setPhase('open');
    reportPromptDisplayed(pendingTypesKey);
  }, [shouldOpen, pendingTypesKey]);

  const onOpenChange = (open: boolean) => {
    if (open) return;
    dismissal.markDismissed();
    setPhase('settled');
  };

  if (phase !== 'open' || prompt?.kind !== 'prompt') {
    return { open: false };
  }

  return { open: true, prompt, onOpenChange };
};
